"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_APPLICATIONS_IMAGE =
	"/image/new-version/molding/insert-molding/applications-of-insert-molding.webp";
const DEFAULT_ADVANTAGES_IMAGE =
	"/image/new-version/molding/insert-molding/insert-molding-process-advantages.webp";

const APPLICATION_ITEM_KEYS = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
	{ titleKey: "app4Title", descKey: "app4Desc" },
] as const;

const ADVANTAGE_ITEM_KEYS = [
	{ titleKey: "adv1Title", descKey: "adv1Desc" },
	{ titleKey: "adv2Title", descKey: "adv2Desc" },
	{ titleKey: "adv3Title", descKey: "adv3Desc" },
] as const;

function CheckBullet() {
	return (
		<div
			className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 sm:h-6 sm:w-6"
			aria-hidden
		>
			<svg className="h-3 w-3 text-white sm:h-3.5 sm:w-3.5" viewBox="0 0 12 10" fill="none" aria-hidden>
				<path
					d="M1 5l3.5 3.5L11 1"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

export interface InsertMoldingApplicationsAdvantagesProps {
	translationNamespace?: string;
	applicationsImageSrc?: string;
	advantagesImageSrc?: string;
}

/**
 * 嵌件成型：上块左文右图（Applications）、下块左图右文（Process Advantages）；lg 双列等高，与 OvermoldingAdvantagesApplications 版式一致
 */
export default function InsertMoldingApplicationsAdvantages({
	translationNamespace = "insertMoldingApplicationsAdvantagesSection",
	applicationsImageSrc = DEFAULT_APPLICATIONS_IMAGE,
	advantagesImageSrc = DEFAULT_ADVANTAGES_IMAGE,
}: InsertMoldingApplicationsAdvantagesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* Applications：大屏左文右图 */}
				<div className="mb-16 flex flex-col gap-8 md:mb-20 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					<div className="flex min-w-0 flex-col lg:order-1 lg:w-1/2">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("applicationsTitle")}
						</h2>
						<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">
							{t("applicationsIntro")}
						</p>
						<ul className="space-y-6 sm:space-y-7">
							{APPLICATION_ITEM_KEYS.map((row) => (
								<li key={row.titleKey} className="flex gap-3 sm:gap-4">
									<CheckBullet />
									<div>
										<p className="mb-1 text-base font-semibold text-black sm:text-lg">{t(row.titleKey)}</p>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(row.descKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
					<div className="relative w-full overflow-hidden lg:order-2 lg:min-h-0 lg:w-1/2">
						<div className="relative aspect-[4/3] w-full lg:absolute lg:inset-0 lg:aspect-auto">
							<Image
								src={applicationsImageSrc}
								alt={t("applicationsImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>

				{/* Process Advantages：大屏左图右文 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					<div className="relative w-full overflow-hidden lg:min-h-0 lg:w-1/2">
						<div className="relative aspect-[4/3] w-full lg:absolute lg:inset-0 lg:aspect-auto">
							<Image
								src={advantagesImageSrc}
								alt={t("advantagesImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
					<div className="flex min-w-0 flex-col lg:w-1/2">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("advantagesTitle")}
						</h2>
						<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">
							{t("advantagesIntro")}
						</p>
						<ul className="space-y-6 sm:space-y-7">
							{ADVANTAGE_ITEM_KEYS.map((row) => (
								<li key={row.titleKey} className="flex gap-3 sm:gap-4">
									<CheckBullet />
									<div>
										<p className="mb-1 text-base font-semibold text-black sm:text-lg">{t(row.titleKey)}</p>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(row.descKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
